<script setup lang="ts">
import { computed } from 'vue'

import LoadingSpinner from '../LoadingSpinner.vue'

interface ButtonSecondaryProps {
  label: string
  disabled?: boolean
  /**
   * A running action: the button shows a spinner in place of its content and cannot be clicked
   * again, the same way `ButtonPrimary` does.
   */
  loading?: boolean
  loadingSkeleton?: boolean
}

const props = defineProps<ButtonSecondaryProps>()

const isSkeleton = computed(() => props.loadingSkeleton)
const isDisabled = computed(() => props.disabled || props.loading)

defineSlots<{
  default(): unknown
}>()
</script>

<template>
  <button
    class="relative inline-flex h-[40px] items-center justify-center gap-2 rounded-[3.125rem] border border-current px-6"
    :class="[
      isSkeleton
        ? 'animate-pulse border-black-200 bg-black-200 text-transparent'
        : isDisabled
          ? 'border-none bg-black-100 text-black-400 hover:cursor-not-allowed'
          : 'border-lochmara-600 text-lochmara-600 hover:cursor-pointer hover:bg-lochmara-100',
    ]"
    :disabled="isDisabled"
    type="button"
  >
    <div
      v-if="$slots['default']"
      class="h-5 w-5 transition-opacity duration-300"
      :class="loading ? 'invisible' : ''"
      :style="{ opacity: isSkeleton ? 0 : 1 }"
    >
      <slot />
    </div>

    <span
      class="transition-opacity duration-300"
      :class="loading ? 'invisible' : ''"
      :style="{ opacity: isSkeleton ? 0 : 1 }"
    >
      {{ props.label }}
    </span>
    <LoadingSpinner v-if="loading" class="margin-auto absolute" size="1rem" />
  </button>
</template>
